import uuid from 'uuid/v4';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

import type { ExperienceAttributes } from './ExperienceEvent.ts';
import {
	CREATE_INLINE_COMMENT_DRAFT_TO_PUBLISH_EXPERIENCE,
	CREATE_INLINE_COMMENT_EXPERIENCE,
	CREATE_INLINE_COMMENT_INIT_EXPERIENCE,
	CREATE_INLINE_COMMENT_ATTACH_COMMENT_EXPERIENCE,
} from './ExperienceName.ts';
import { getExperienceTracker } from './getExperienceTracker.ts';
import { collectAll } from './ExperienceEventCollector.ts';

export type CreateInlineCommentCompoundExperienceEntryPoint =
	/**
	 * Used when a user creates a new comment by highlighting text and clicking the create comment button in the hover menu.
	 * Triggered when the "Comment" button is clicked
	 */
	| 'highlightActions'
	/**
	 * Used when a user creates a new comment by hovering over media and clicking the create comment button in the menu.
	 * Triggered when the "Comment" button is clicked
	 */
	| 'highlightActionsSimple'
	/**
	 * Used when a user creates a new comment using a keyboard shortcut.
	 * Triggered when the shortcut is typed
	 */
	| 'keyboardShortcut'
	/**
	 * Used when a user clicks on the nudgebar, on the right in the renderer
	 */
	| 'nudgebar';

let commonCreateInlineCommentExperiencesAttributes: ExperienceAttributes = {};
let runningExperiences: string[] = [];
// Note: The soft failure is set on the first soft failure not updated following
let softFailure: { error: Error; pointOfFailure: string } | undefined = undefined;
let publishFailed = false;
let publishSuccessCount: number = 0;

function getCreateInlineCommentExperienceDebug(experienceName: string) {
	function createInlineCommentExperienceDebug({
		error,
		createAnalyticsEvent,
		extraAttributes,
	}: {
		createAnalyticsEvent: CreateUIAnalyticsEvent;
		error: Error;
		extraAttributes?: ExperienceAttributes;
	}) {
		const experienceTracker = getExperienceTracker();

		const experienceAttributes = experienceTracker.getExperienceState(
			CREATE_INLINE_COMMENT_EXPERIENCE,
		);
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action: 'error',
				actionSubject: CREATE_INLINE_COMMENT_EXPERIENCE,
				source: experienceName,
				attributes: {
					...experienceAttributes,
					...commonCreateInlineCommentExperiencesAttributes,
					...extraAttributes,

					error: error.message || String(error),
					stack: error.stack,
				},
			},
		}).fire();
	}
	return createInlineCommentExperienceDebug;
}

/**
 * This is used for the create inline comment parent and children experiences,
 * and manages some internal state connecting the parent and child experiences.
 *
 * ```ts
 * // start the parent experience
 * createInlineCommentCompoundExperience.start()
 * ```
 *
 * ```ts
 * // attach attributes across all experiences
 * createInlineCommentCompoundExperience.addCommonAttributes({ example: 'value' })
 * ```
 *
 * ```ts
 * // start a child experience
 * createInlineCommentCompoundExperience.initExperience.start()
 * ```
 *
 * Each child experience has additional methods for completing, failing, and debugging.
 */
export const createInlineCommentCompoundExperience = {
	start: function createInlineCommentCompoundExperience({
		attributes,
	}: {
		attributes: /**
		 * Editor Notes: In the Editor -- we don't have access to an annotationId until
		 * later in the experience (on "publish").
		 */

		| {
					pageClass: 'editor';
					commentType: 'inline';
					annotationId?: string;
					entryPoint?: CreateInlineCommentCompoundExperienceEntryPoint;
			  }
			| {
					pageClass: 'editor';
					commentType: 'block';
					blockType: 'media';
					annotationId?: undefined;
					entryPoint?: CreateInlineCommentCompoundExperienceEntryPoint;
			  }
			| {
					pageClass: 'renderer';
					commentType: 'inline';
					annotationId: string;
					entryPoint?: CreateInlineCommentCompoundExperienceEntryPoint;
			  }
			| {
					pageClass: 'renderer';
					commentType: 'block';
					blockType: 'media';
					annotationId: string;
					entryPoint?: CreateInlineCommentCompoundExperienceEntryPoint;
			  };
	}) {
		// reset common experience state when starting a new parent experience
		runningExperiences = [CREATE_INLINE_COMMENT_EXPERIENCE];
		commonCreateInlineCommentExperiencesAttributes = {};
		softFailure = undefined;
		publishFailed = false;
		publishSuccessCount = 0;

		const compoundTaskId = uuid();
		const experienceTracker = getExperienceTracker();

		experienceTracker.start({
			name: CREATE_INLINE_COMMENT_EXPERIENCE,
			id: compoundTaskId,
			attributes: {
				...attributes,
				compoundTaskId,
			},
			collect: (events, experience) => {
				collectAll([
					CREATE_INLINE_COMMENT_INIT_EXPERIENCE,
					CREATE_INLINE_COMMENT_DRAFT_TO_PUBLISH_EXPERIENCE,
				])(events, experience);
			},
		});

		commonCreateInlineCommentExperiencesAttributes = {
			compoundTaskId,
			annotationId: attributes.annotationId,
			pageClass: attributes.pageClass,
			entryPoint: attributes.entryPoint,
		};
	},
	debug: getCreateInlineCommentExperienceDebug(CREATE_INLINE_COMMENT_EXPERIENCE),
	addCommonAttributes: (commonAttributes: /**
	 * Explicitly typing the expected added attributes to mitigate
	 * unexpected usage.
	 */
	{
		[key: string]: string | number | boolean | string[] | number[];
	}) => {
		const experienceTracker = getExperienceTracker();

		commonCreateInlineCommentExperiencesAttributes = {
			...commonCreateInlineCommentExperiencesAttributes,
			...commonAttributes,
		};
		for (const runningExperience of runningExperiences) {
			experienceTracker.updateAttributes({
				name: runningExperience,
				attributes: commonCreateInlineCommentExperiencesAttributes,
			});
		}
	},
	attachCommonAttributes: (experienceName: string) => {
		const experienceTracker = getExperienceTracker();

		experienceTracker.updateAttributes({
			name: experienceName,
			attributes: commonCreateInlineCommentExperiencesAttributes,
		});
	},
	initExperience: {
		start: () => {
			runningExperiences.push(CREATE_INLINE_COMMENT_INIT_EXPERIENCE);
			const experienceTracker = getExperienceTracker();

			experienceTracker.start({
				name: CREATE_INLINE_COMMENT_INIT_EXPERIENCE,
				timeout: 5000,
				attributes: {
					...commonCreateInlineCommentExperiencesAttributes,
				},
			});
			createInlineCommentCompoundExperience.attachCommonAttributes(
				CREATE_INLINE_COMMENT_INIT_EXPERIENCE,
			);
		},
		fail: (error: Error) => {
			const experienceTracker = getExperienceTracker();
			experienceTracker.fail({
				name: CREATE_INLINE_COMMENT_INIT_EXPERIENCE,
				error,
			});
		},
		softFail: (error: Error) => {
			if (!softFailure) {
				softFailure = { error, pointOfFailure: 'init' };
			}
			const experienceTracker = getExperienceTracker();
			experienceTracker.succeed({
				name: CREATE_INLINE_COMMENT_INIT_EXPERIENCE,
				attributes: {
					softFailure: error.message,
				},
			});
		},
		abort: ({
			abortReason,
		}: {
			abortReason: 'Draft annotation removed from document' | 'Create Comment UI dismissed';
		}) => {
			const experienceTracker = getExperienceTracker();
			experienceTracker.abort({
				name: CREATE_INLINE_COMMENT_INIT_EXPERIENCE,
				reason: abortReason,
			});
		},
		/**
		 * This completes the init experience and starts the draft to publish experience.
		 */
		complete: () => {
			const experienceTracker = getExperienceTracker();

			experienceTracker.succeed({
				name: CREATE_INLINE_COMMENT_INIT_EXPERIENCE,
			});
		},
		debug: getCreateInlineCommentExperienceDebug(CREATE_INLINE_COMMENT_INIT_EXPERIENCE),
		debugPoint: (message: string, attributes?: ExperienceAttributes) => {
			const experienceTracker = getExperienceTracker();
			experienceTracker.attachDebugPoint({
				name: CREATE_INLINE_COMMENT_INIT_EXPERIENCE,
				message,
				attributes,
			});
		},
	},
	draftToPublishExperience: {
		start: () => {
			runningExperiences.push(CREATE_INLINE_COMMENT_DRAFT_TO_PUBLISH_EXPERIENCE);
			const experienceTracker = getExperienceTracker();

			experienceTracker.start({
				name: CREATE_INLINE_COMMENT_DRAFT_TO_PUBLISH_EXPERIENCE,
				attributes: commonCreateInlineCommentExperiencesAttributes,
			});
			createInlineCommentCompoundExperience.attachCommonAttributes(
				CREATE_INLINE_COMMENT_DRAFT_TO_PUBLISH_EXPERIENCE,
			);
		},
		publishFailed: (error: Error) => {
			publishFailed = true;
			if (!softFailure) {
				softFailure = { error, pointOfFailure: 'publish-failed' };
			}
		},
		publishSucceeded: () => {
			publishSuccessCount += 1;
		},
		abort: ({
			abortReason,
		}: {
			abortReason:
				| 'Create Comment UI dismissed'
				| 'Unable to apply annotation to document'
				| 'User does not have space permission to create comments';
		}) => {
			const experienceTracker = getExperienceTracker();
			experienceTracker.abort({
				name: CREATE_INLINE_COMMENT_DRAFT_TO_PUBLISH_EXPERIENCE,
				reason: abortReason,
			});
		},
		fail: (error: Error, attributes?: ExperienceAttributes) => {
			const experienceTracker = getExperienceTracker();
			experienceTracker.fail({
				name: CREATE_INLINE_COMMENT_DRAFT_TO_PUBLISH_EXPERIENCE,
				error,
				attributes,
			});
		},
		stopOnError: (error: Error, attributes?: ExperienceAttributes) => {
			const experienceTracker = getExperienceTracker();
			experienceTracker.stopOnError({
				name: CREATE_INLINE_COMMENT_DRAFT_TO_PUBLISH_EXPERIENCE,
				error,
				attributes,
			});
		},
		dismissed: () => {
			const experienceTracker = getExperienceTracker();
			if (publishSuccessCount > 0) {
				experienceTracker.fail({
					name: CREATE_INLINE_COMMENT_DRAFT_TO_PUBLISH_EXPERIENCE,
					error: new Error('Draft comment dismissed after publish success'),
				});
			} else if (publishFailed) {
				experienceTracker.fail({
					name: CREATE_INLINE_COMMENT_DRAFT_TO_PUBLISH_EXPERIENCE,
					error: new Error('Draft comment dismissed after publish attempt'),
				});
			} else {
				experienceTracker.abort({
					name: CREATE_INLINE_COMMENT_DRAFT_TO_PUBLISH_EXPERIENCE,
					reason: 'Draft comment dismissed',
				});
			}
		},
		softFail: (error: Error) => {
			const experienceTracker = getExperienceTracker();

			if (!softFailure) {
				softFailure = { error, pointOfFailure: 'multiple-saves-without-error' };
			}

			experienceTracker.succeed({
				name: CREATE_INLINE_COMMENT_DRAFT_TO_PUBLISH_EXPERIENCE,
				attributes: {
					softFailure: error.message,
				},
			});
		},
		complete: () => {
			const experienceTracker = getExperienceTracker();

			if (softFailure) {
				experienceTracker.succeed({
					name: CREATE_INLINE_COMMENT_DRAFT_TO_PUBLISH_EXPERIENCE,
					attributes: {
						softFailure: softFailure.error.message,
						pointOfFailure: softFailure.pointOfFailure,
					},
				});
				return;
			}

			experienceTracker.succeed({
				name: CREATE_INLINE_COMMENT_DRAFT_TO_PUBLISH_EXPERIENCE,
			});
		},
		debug: getCreateInlineCommentExperienceDebug(CREATE_INLINE_COMMENT_DRAFT_TO_PUBLISH_EXPERIENCE),
		debugPoint: (message: string, attributes?: ExperienceAttributes) => {
			const experienceTracker = getExperienceTracker();
			experienceTracker.attachDebugPoint({
				name: CREATE_INLINE_COMMENT_DRAFT_TO_PUBLISH_EXPERIENCE,
				message,
				attributes,
			});
		},
	},
	attachCommentExperience: {
		start: () => {
			runningExperiences.push(CREATE_INLINE_COMMENT_ATTACH_COMMENT_EXPERIENCE);
			const experienceTracker = getExperienceTracker();

			experienceTracker.start({
				name: CREATE_INLINE_COMMENT_ATTACH_COMMENT_EXPERIENCE,
				// If the view comment does not load in 6 seconds, it's
				// an indicator something has gone wrong.
				timeout: 6000,
				attributes: commonCreateInlineCommentExperiencesAttributes,
			});
			createInlineCommentCompoundExperience.attachCommonAttributes(
				CREATE_INLINE_COMMENT_ATTACH_COMMENT_EXPERIENCE,
			);
		},
		fail: (error: Error, attributes?: ExperienceAttributes) => {
			const experienceTracker = getExperienceTracker();
			experienceTracker.fail({
				name: CREATE_INLINE_COMMENT_ATTACH_COMMENT_EXPERIENCE,
				error,
				attributes,
			});
		},
		complete: () => {
			const experienceTracker = getExperienceTracker();
			if (softFailure) {
				experienceTracker.succeed({
					name: CREATE_INLINE_COMMENT_ATTACH_COMMENT_EXPERIENCE,
					attributes: {
						softFailure: softFailure.error.message,
						pointOfFailure: softFailure.pointOfFailure,
					},
				});
				return;
			}
			experienceTracker.succeed({
				name: CREATE_INLINE_COMMENT_ATTACH_COMMENT_EXPERIENCE,
			});
		},
		debug: getCreateInlineCommentExperienceDebug(CREATE_INLINE_COMMENT_ATTACH_COMMENT_EXPERIENCE),
		debugPoint: (message: string, attributes?: ExperienceAttributes) => {
			const experienceTracker = getExperienceTracker();
			experienceTracker.attachDebugPoint({
				name: CREATE_INLINE_COMMENT_ATTACH_COMMENT_EXPERIENCE,
				message,
				attributes,
			});
		},
	},
};
